import { faArrowUpRightFromSquare, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';

import DocumentLink from '../../components/DocumentLink/DocumentLink';
import Icon from '../../components/Icon/Icon';
import Link from '../../components/Link/Link';
import ListField from '../../components/ListField/ListField';
import Subtitle from '../../components/Subtitle/Subtitle';
import Summary from '../../components/Summary/Summary';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { useAppSelector } from '../../hooks';
import useTranslation from '../../hooks/useTranslation';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import ChildAgeGroupSummaryRows from './ChildAgeGroupSummaryRows';
import ConsolidationsSummaryRow from './ConsolidationsSummaryRow';
import FieldSummaryRow from './FieldSummaryRow';

const SubmitPageSidePanelDK = () => {
    const translate = useTranslation();
    const hasCoApplicant = useAppSelector(selectFieldStateByName)('coapplicantToggle')?.value === 'true';
    const consolidations = useAppSelector((state) => state.form.consolidations);

    return (
        <>
            <Subtitle text={translate('page.submit.subtitle.details')} />
            <Summary label={translate('field.section.about-the-loan')}>
                <FieldSummaryRow
                    name="amount"
                    label={translate('field.amount.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="tenor"
                    label={translate('field.tenor.label')}
                    formatValue={(value) => `${value / 12} ${value / 12 > 1 ? translate('years') : translate('year')}`}
                />
                <FieldSummaryRow name="loanReason" label={translate('page.submit.purpose-of-loan.label')} />
            </Summary>

            {consolidations?.length > 0 && (
                <Summary label={translate('field.section.consolidation.label')} expand={false}>
                    <ConsolidationsSummaryRow consolidations={consolidations} />
                </Summary>
            )}

            <Summary label={translate('field.section.about-you')} expand={false}>
                <FieldSummaryRow
                    name="applicantGovernmentId"
                    label={translate('page.submit.applicant-government-id.label')}
                />
                <FieldSummaryRow
                    name="applicantEmailAddress"
                    label={translate('page.submit.applicant-email-address.label')}
                />
                <FieldSummaryRow
                    name="applicantMobileNumber"
                    label={translate('page.submit.applicant-mobile-number.label')}
                />
                <FieldSummaryRow
                    name="applicantMaritalStatus"
                    label={translate('page.submit.applicant-marital-status.label')}
                />
                <ChildAgeGroupSummaryRows />
                <FieldSummaryRow
                    name="applicantHabitationForm"
                    label={translate('page.submit.applicant-habitation-form.label')}
                />
                <FieldSummaryRow
                    name="applicantHabitationYear"
                    label={translate('page.submit.applicant-habitation-year.label')}
                    formatValue={(value) => dayjs(value).format('MM/YYYY')}
                />
                <FieldSummaryRow
                    name="applicantAccomodationCost"
                    label={translate('page.submit.applicant-rent-share.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantShareOfExpenses"
                    label={translate('page.submit.applicant-share-of-expenses.label')}
                    formatValue={(value) => `${value}%`}
                />
                <FieldSummaryRow
                    name="applicantEmploymentType"
                    label={translate('page.submit.applicant-employment-type.label')}
                />
                <FieldSummaryRow
                    name="applicantNetMonthlyIncome"
                    label={translate('field.applicant-net-monthly-income.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantYearlyGrossIncome"
                    label={translate('page.submit.applicant-yearly-gross-income.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantMonthlyIncomeOther"
                    label={translate('page.submit.applicant-monthly-income-other.label')}
                    formatValue={(value) => formatNumberByThousands(value)}
                />
                <FieldSummaryRow
                    name="applicantMortgageDebt"
                    label={translate('page.submit.applicant-mortage-debt.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="householdLivingExpenses"
                    label={translate('page.submit.household-living-expenses.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="householdLoanExpenses"
                    label={translate('page.submit.household-loan-expenses.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantNumberOfCars"
                    label={translate('page.submit.applicant-number-of-cars.label')}
                />
                <FieldSummaryRow
                    name="applicantNewDebtThisYear"
                    label={translate('page.submit.applicant-new-debt-this-year.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCarLoanSum"
                    label={translate('page.submit.applicant-car-loan-sum.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCarLoanMonthlyCost"
                    label={translate('page.submit.applicant-car-loan-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCarLeasingMonthlyCost"
                    label={translate('page.submit.applicant-car-leasing-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantStudentLoanSum"
                    label={translate('page.submit.applicant-student-loan-sum.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantStudentLoanMonthlyCost"
                    label={translate('page.submit.applicant-student-loan-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow name="applicantDankort" label={translate('page.submit.applicant-dankort.label')} />
            </Summary>
            {hasCoApplicant && (
                <Summary label={translate('page.submit.about-coapplicant.label')} expand={false}>
                    <FieldSummaryRow
                        name="coapplicantGovernmentId"
                        label={translate('page.submit.coapplicant-government-id.label')}
                    />
                    <FieldSummaryRow
                        name="coapplicantEmailAddress"
                        label={translate('page.submit.coapplicant-email-adress.label')}
                    />
                    <FieldSummaryRow
                        name="coapplicantMobileNumber"
                        label={translate('page.submit.coapplicant-mobile-number.label')}
                    />
                </Summary>
            )}

            <Subtitle text={translate('page.submit.subtitle.documents')} />
            <ListField
                name="commonTermsAndSekki"
                items={[
                    {
                        index: '1',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(translate('page.submit.link.common-terms'), '_blank')}>
                                <Typography variant="body2">{translate('page.submit.label.common-terms')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                    {
                        index: '2',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(translate('page.submit.link.sekki'), '_blank')}>
                                <Typography variant="body2">{translate('page.submit.label.sekki')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                ]}
            />

            <Subtitle text={translate('page.submit.subtitle.gdpr')} />
            <Typography variant="body2"> {translate('page.submit.body-text.gdpr')}</Typography>
            <Typography variant="body2">
                <DocumentLink href={translate('page.submit.link.gdpr')} text={translate('page.submit.label.gdpr')} />
            </Typography>
        </>
    );
};

export default SubmitPageSidePanelDK;
